import apiClient from '@app/config/axios.config'
import { Endpoint } from '@app/config/endpoints'
import { DatasetRow } from '@app/modules/Home/Data/types'

export const MULTIPART_THRESHOLD_BYTES = 100 * 1024 * 1024 // 100 MB as per aws rec
export const MULTIPART_CHUNK_SIZE = 50 * 1024 * 1024 // 50 MB per part

export interface DatasetMetadata {
  name: string
  description: string
  geometryType: 'line' | 'point' | 'polygon'
  metricType: 'count' | 'area' | 'length'
  source?: string
  spatialExtent?: string
  icon?: string
  displaySource?: string
  type?: string
  utility?: string
  lineSegmentLength?: number
  polygonSegmentArea?: number
  changeToPoint?: boolean
  unit?: string
  license?: 'Restricted' | 'Controlled' | 'Open'
}

const buildMetadataPayload = (metadata: DatasetMetadata) => ({
  name: metadata.name,
  description: metadata.description,
  source: metadata.source,
  spatial_extent: metadata.spatialExtent,
  icon: metadata.icon,
  display_source: metadata.displaySource,
  type: metadata.type,
  utility: metadata.utility,
  geometry_type: metadata.geometryType,
  line_segment_length: metadata.lineSegmentLength,
  polygon_segment_area: metadata.polygonSegmentArea,
  change_to_point: metadata.changeToPoint,
  unit: metadata.unit,
  metric_type: metadata.metricType,
  license: metadata.license,
})

export interface UploadFile {
  filename: string
  contentType?: string
  data: string
}

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve((reader.result as string).split(',')[1])
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

export const fileToUploadFile = async (file: File): Promise<UploadFile> => ({
  filename: file.name,
  contentType: file.type || undefined,
  data: await blobToBase64(file),
})

export const uploadDataset = async (
  files: File[],
  metadata: DatasetMetadata,
): Promise<{ library_id: string }> => {
  const uploadFiles = await Promise.all(files.map(fileToUploadFile))
  const { data } = await apiClient.post<{ library_id: string }>(Endpoint.datasetUpload, {
    ...buildMetadataPayload(metadata),
    files: uploadFiles,
  })
  return data
}

export const initiateMultipartUpload = async (
  file: File,
  name: string,
  total_parts: number,
): Promise<{ uploadId: string; key: string; presignedUrls: string[] }> => {
  const { data } = await apiClient.post<{ uploadId: string; key: string; presignedUrls: string[] }>(
    Endpoint.datasetUploadMultipartInitiate,
    { name, filename: file.name, contentType: file.type || undefined, total_parts },
  )
  return data
}

export const uploadPartToS3 = async (presignedUrl: string, chunk: Blob): Promise<string> => {
  // use fetch so don't send our axios instance's auth headers to presigned S3 urls
  const response = await fetch(presignedUrl, { method: 'PUT', body: chunk })
  if (!response.ok) throw new Error(`S3 part upload failed: ${response.status}`)
  const etag = response.headers.get('ETag')
  if (!etag) throw new Error('Missing ETag in S3 response')
  return etag
}

export const completeMultipartUpload = async (
  key: string,
  uploadId: string,
  parts: { PartNumber: number; ETag: string }[],
  metadata: DatasetMetadata,
  companions: UploadFile[],
): Promise<{ library_id: string }> => {
  const { data } = await apiClient.post<{ library_id: string }>(
    Endpoint.datasetUploadMultipartComplete,
    {
      key,
      uploadId,
      parts,
      ...buildMetadataPayload(metadata),
      companions,
    },
  )
  return data
}

export const abortMultipartUpload = async (key: string, uploadId: string): Promise<void> => {
  await apiClient.post(Endpoint.datasetUploadMultipartAbort, { key, uploadId })
}

export const initiateMultipartUpdateUpload = async (
  id: string,
  file: File,
  name: string,
  total_parts: number,
): Promise<{ uploadId: string; key: string; presignedUrls: string[] }> => {
  const { data } = await apiClient.post<{ uploadId: string; key: string; presignedUrls: string[] }>(
    `${Endpoint.datasetList}/${id}/multipart/initiate`,
    { name, filename: file.name, contentType: file.type || undefined, total_parts },
  )
  return data
}

export const completeMultipartUpdate = async (
  id: string,
  key: string,
  uploadId: string,
  parts: { PartNumber: number; ETag: string }[],
  metadata?: Partial<DatasetMetadata>,
  companions?: UploadFile[],
  config?: Record<string, unknown>,
): Promise<{ library_id: string }> => {
  const { data } = await apiClient.post<{ library_id: string }>(
    `${Endpoint.datasetList}/${id}/multipart/complete`,
    {
      key,
      uploadId,
      parts,
      ...(metadata ? buildMetadataPayload(metadata as DatasetMetadata) : {}),
      companions,
      config,
    },
  )
  return data
}

export const uploadElementConfig = async (
  name: string,
  config: Record<string, unknown>,
): Promise<void> => {
  await apiClient.post(Endpoint.datasetElementConfig, { name, config })
}

export const updateDataset = async (
  id: string,
  {
    metadata,
    config,
    files,
  }: {
    metadata?: Partial<DatasetMetadata>
    config?: Record<string, unknown>
    files?: UploadFile[]
  },
): Promise<DatasetRow> => {
  const { data } = await apiClient.put<DatasetRow>(`${Endpoint.datasetList}/${id}`, {
    ...(metadata ? buildMetadataPayload(metadata as DatasetMetadata) : {}),
    config,
    files,
  })
  return data
}

export const getDatasets = async (): Promise<DatasetRow[]> => {
  const { data } = await apiClient.get<DatasetRow[]>(Endpoint.datasetList)
  return data
}

export const getDatasetById = async (id: string): Promise<DatasetRow | null> => {
  const { data } = await apiClient.get<DatasetRow | null>(`${Endpoint.datasetList}/${id}`)
  return data
}
